<template>
  <div class="login-register-content">
    <Form ref="registerForm" v-slot="{ meta }" :validation-schema="formSchema" @submit="submitRegister">
      <div class="wd-mb-6">
        <div class="form__input mb-4">
          <p class="input-label mb-2">{{ $t('all_account') }}</p>
          <Field v-slot="{ field, errorMessage, handleChange, validate }" v-model="email" name="email">
            <v-text-field
              :model-value="field.value"
              :placeholder="$t('account_hint')"
              :min-height="40"
              :color="'orange50'"
              :error-messages="errorMessage"
              hide-details="auto"
              variant="outlined"
              density="compact"
              required
              @update:model-value="handleChange"
              @blur="validate"
            />
          </Field>
        </div>

        <div class="form__input mb-4">
          <p class="input-label mb-2">{{ $t('all_pwd') }}</p>
          <Field v-slot="{ field, errorMessage, handleChange, validate }" v-model="password" name="password">
            <v-text-field
              :model-value="field.value"
              :type="showPassword ? 'text' : 'password'"
              :placeholder="$t('pwd_hint')"
              :hint="$t('pwd_remark')"
              :color="'orange50'"
              :min-height="40"
              :error-messages="errorMessage"
              persistent-hint
              variant="outlined"
              density="compact"
              required
              @update:model-value="handleChange"
              @blur="validate"
            >
              <template #append-inner>
                <div @click="showPassword = !showPassword">
                  <funnow-icon class="dna-icon wd-w-5" max-width="20" :image-path="`dna-icon-eyes-${showPassword ? 'close' : 'open'}.svg`" />
                </div>
              </template>
            </v-text-field>
          </Field>
        </div>

        <div class="form__input mb-4">
          <Field v-slot="{ field, errorMessage, handleChange, validate }" v-model="secPassword" name="confirmPassword">
            <v-text-field
              :model-value="field.value"
              :type="showSecPassword ? 'text' : 'password'"
              :placeholder="$t('pwd_refilled_hint')"
              :min-height="40"
              :color="'orange50'"
              :error-messages="errorMessage"
              hide-details="auto"
              variant="outlined"
              density="compact"
              required
              @update:model-value="handleChange"
              @blur="validate"
            >
              <template #append-inner>
                <div @click="showSecPassword = !showSecPassword">
                  <funnow-icon class="dna-icon wd-w-5" max-width="20" :image-path="`dna-icon-eyes-${showSecPassword ? 'close' : 'open'}.svg`" />
                </div>
              </template>
            </v-text-field>
          </Field>
        </div>

        <member-consent-checkboxes v-model="userContract" />
      </div>

      <dialog-error v-model:show-status="hasError" :error="error" />

      <v-btn
        class="register__btn mt-3 mb-5"
        :class="{ disabled: disabledRegisterBtn || !meta.valid }"
        :height="40"
        type="submit"
        color="orange50"
        variant="flat"
        block
      >
        <loading v-if="registerLoading" />
        <span v-else> {{ $t('all_register') }} </span>
      </v-btn>
    </Form>

    <div class="dialog-footer text-center">
      <div class="question-hint">
        <span> {{ $t('register_current_user') }} </span>
        <span class="login-text wd-text-orange50" @click="changeLoginPage"> {{ $t('all_login') }} </span>
      </div>

      <v-divider class="my-5" />

      <v-btn class="other-way__btn" :height="24" color="orange50" variant="text" @click="changeIndexPage">
        {{ $t('login_other_method') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { object as yupObject } from 'yup'
import { RegionIdMap } from '@/view-models/region'
import { useMemberConsent } from '@/composition/useMemberConsent'

import Loading from '@/components/icons/loading'
import FunnowIcon from '@/components/icons/funnow-icon'
import DialogError from '@/components/notice/error/dialog-error'
import MemberConsentCheckboxes from '@/components/login/MemberConsentCheckboxes.vue'

export default {
  name: 'RegisterIndex',
  components: {
    Loading,
    FunnowIcon,
    MemberConsentCheckboxes,
    DialogError,
  },
  emits: ['changeAuthStep', 'success'],
  setup() {
    const rules = useFieldRules()
    const formSchema = computed(() =>
      yupObject({
        email: rules.email(),
        password: rules.password(),
        confirmPassword: rules.confirmPassword(),
      })
    )

    const { userContract, isConsentValid } = useMemberConsent()

    return {
      formSchema,
      userContract,
      isConsentValid,
    }
  },
  data() {
    return {
      registerLoading: false,
      showPassword: false,
      showSecPassword: false,
      disableBtn: true,
      email: '',
      password: '',
      secPassword: '',
      hasError: false,
      error: {},
    }
  },
  computed: {
    disabledRegisterBtn() {
      return this.registerLoading || !this.isConsentValid
    },
  },
  methods: {
    clearError() {
      this.hasError = false
      this.error = {}
    },
    changeIndexPage() {
      this.$emit('changeAuthStep', 'index')
    },
    changeLoginPage() {
      this.$emit('changeAuthStep', 'login')
    },
    getRegisterData() {
      return {
        username: this.email,
        password: this.password,
        email: this.email,
        name: this.email.split('@')[0],
        gender: null,
        currency: 0,
        userContract: this.userContract,
      }
    },
    async submitRegister() {
      const { valid } = await this.$refs.registerForm?.validate()

      if (valid && !this.registerLoading && this.isConsentValid) {
        this.registerLoading = true
        this.dispatchRegister()
      }
    },
    async dispatchRegister() {
      this.clearError()
      const registerData = this.getRegisterData()

      try {
        const isEliteRedemptionPage = this.$route.name.includes('elite-redemption')
        const payload = {
          data: registerData,
          ...(isEliteRedemptionPage ? { regionId: RegionIdMap.KualaLumpur } : null),
        }

        const registerRes = await this.$store.dispatch('authentication/register', payload)
        this.registerLoading = false

        if (registerRes.code !== 0) {
          return
        }

        this.sendTrackingCode(registerRes.data.uid)
        this.$emit('success')
      } catch (error) {
        this.registerLoading = false
        this.hasError = true
        this.error = error
        console.error(error)
      }
    },
    sendTrackingCode(UID) {
      if (UID) {
        this.$gtm.push({
          event: 'registration',
          authenticationMethod: 'email',
          userId: UID,
        })
      }
    },
  },
}
</script>
<style scoped lang="scss">
.login-register-content {
  // max-height: calc(75vh - 146px);
  overflow-y: auto;
  .form-block {
    .form__input {
      margin-bottom: 16px;

      .input-label {
        font-size: 13px;
      }
    }
  }

  .register__btn {
    font-size: 17px;
    &.disabled {
      background-color: $orange30 !important;
      pointer-events: none;
    }
  }

  .dialog-footer {
    .question-hint {
      line-height: 24px;
      .login-text {
        cursor: pointer;
      }
    }
    .question-hint,
    .other-way__btn {
      font-size: 13px;
    }
  }
}
</style>
